'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import {
  Menu, X, MapPin, BookOpen, Users, Info, Package, UserCircle,
  LogOut, LayoutDashboard, LifeBuoy, Rocket, BarChart2, MessageSquare,
  Activity, CreditCard, ChevronDown, Package2, Gauge, Settings, Handshake, LayoutGrid, Crown, Gift,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import NotificationBell from '@/components/NotificationBell';


// Primary public links — always visible
const primaryNavLinks = [
  { label: 'Get Started', href: '/welcome', icon: Rocket },
  { label: 'Services', href: '/placements', icon: BookOpen },
  { label: 'Help', href: '/help', icon: LifeBuoy },
];

// Secondary public links — grouped under "Learn More" dropdown
const learnMoreLinks = [
  { label: 'How It Works', href: '/home#how-it-works', icon: Info },
  { label: 'Regions & Pricing', href: '/regions-pricing', icon: MapPin },
  { label: 'Partners', href: '/home#partners', icon: Users },
  { label: 'Business Exchange', href: '/business-exchange', icon: Handshake },
];

// Auth: Orders dropdown
const ordersLinks = [
  { label: 'Customer Hub', href: '/customer-hub', icon: LayoutGrid },
  { label: 'My Dashboard', href: '/customer-dashboard', icon: Gauge },
  { label: 'Advertiser Dashboard', href: '/advertiser-dashboard', icon: LayoutDashboard },
  { label: 'My Campaigns', href: '/my-campaigns', icon: BookOpen },
  { label: 'My Orders', href: '/my-orders', icon: Package },
  { label: 'Order Tracking', href: '/order-tracking', icon: Activity },
  { label: 'Order History', href: '/order-history', icon: BarChart2 },
  { label: 'Order Dashboard', href: '/order-dashboard', icon: LayoutDashboard },
];

// Auth: Account dropdown
const accountLinks = [
  { label: 'Account Settings', href: '/account-settings', icon: Settings },
  { label: 'Billing', href: '/billing-dashboard', icon: CreditCard },
  { label: 'My Membership', href: '/my-membership', icon: Crown },
  { label: 'Partner Benefits', href: '/advertiser-dashboard?tab=partner-benefits', icon: Gift },
  { label: 'Support', href: '/support', icon: MessageSquare },
  { label: 'My Profile', href: '/profile', icon: UserCircle },
];

// ── Reusable dropdown component ──────────────────────────────────────────────
interface DropdownItem {
  label: string;
  href: string;
  icon: React.ElementType;
}

interface NavDropdownProps {
  label: string;
  icon?: React.ElementType;
  items: DropdownItem[];
  pathname: string;
}

function NavDropdown({ label, icon: Icon, items, pathname }: NavDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isActive = items.some((i) => pathname === i.href);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
          isActive ? 'text-brand-300 bg-brand-600/10' : 'text-slate-400 hover:text-white hover:bg-white/5'
        }`}
      >
        {Icon && <Icon size={14} className="shrink-0" />}
        {label}
        <ChevronDown
          size={13}
          className={`ml-0.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1.5 w-52 rounded-xl border border-brand-900/40 bg-navy-900/95 backdrop-blur-md shadow-xl py-1.5 z-50 animate-fade-in">
          {items.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition-colors ${
                pathname === item.href
                  ? 'text-brand-300 bg-brand-600/10' :'text-slate-300 hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon size={15} className="text-brand-400 shrink-0" />
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PublicTopbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileLearnOpen, setMobileLearnOpen] = useState(false);
  const [mobileOrdersOpen, setMobileOrdersOpen] = useState(false);
  const [mobileAccountOpen, setMobileAccountOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { loading, isAuthenticated, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      router?.push('/home');
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-navy-900/95 backdrop-blur-md border-b border-brand-900/40 shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <Link href="/home" className="flex items-center gap-2.5 shrink-0">
            <AppLogo size={96} />
            <span className="font-display font-700 text-lg tracking-tight">
              <span className="text-white">Zip</span>
              <span className="text-gradient">Ad</span>
              <span className="text-white">Book</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-0.5">
            {/* Primary public links */}
            {primaryNavLinks.map((link) => (
              <Link
                key={`nav-${link.label}`}
                href={link.href}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  pathname === link.href
                    ? 'text-brand-300 bg-brand-600/10' :'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Learn More dropdown (secondary public links) */}
            <NavDropdown
              label="Learn More"
              items={learnMoreLinks}
              pathname={pathname}
            />

            {/* Authenticated: Orders dropdown */}
            {isAuthenticated && (
              <NavDropdown
                label="Orders"
                icon={Package2}
                items={ordersLinks}
                pathname={pathname}
              />
            )}

            {/* Authenticated: Account dropdown */}
            {isAuthenticated && (
              <NavDropdown
                label="Account"
                icon={UserCircle}
                items={accountLinks}
                pathname={pathname}
              />
            )}
          </nav>

          {/* Right Actions */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:+13212653177"
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
            >
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              +1 (321) 265-3177
            </a>
            {isAuthenticated && <NotificationBell variant="light" />}
            {!loading && (
              isAuthenticated ? (
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium btn-outline"
                >
                  <LogOut size={14} />
                  Sign Out
                </button>
              ) : (
                <Link
                  href="/sign-up-login"
                  className="px-4 py-2 text-sm font-medium btn-outline font-semibold"
                >
                  Sign In
                </Link>
              )
            )}
            <Link
              href="/order-wizard"
              className="px-5 py-2 text-sm font-semibold btn-primary"
            >
              Book an Ad
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-navy-900/98 backdrop-blur-md border-t border-brand-900/30 animate-fade-in">
          <div className="px-4 py-4 space-y-1">

            {/* Primary public links */}
            {primaryNavLinks.map((link) => (
              <Link
                key={`mobile-nav-${link.label}`}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
              >
                <link.icon size={16} className="text-brand-400" />
                {link.label}
              </Link>
            ))}

            {/* Learn More accordion */}
            <div>
              <button
                onClick={() => setMobileLearnOpen((v) => !v)}
                className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
              >
                <span className="flex items-center gap-3">
                  <Info size={16} className="text-brand-400" />
                  Learn More
                </span>
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-200 ${mobileLearnOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {mobileLearnOpen && (
                <div className="pl-8 space-y-0.5">
                  {learnMoreLinks.map((link) => (
                    <Link
                      key={`mobile-learn-${link.label}`}
                      href={link.href}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <link.icon size={15} className="text-brand-400" />
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Authenticated: Orders accordion */}
            {isAuthenticated && (
              <div>
                <button
                  onClick={() => setMobileOrdersOpen((v) => !v)}
                  className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                >
                  <span className="flex items-center gap-3">
                    <Package2 size={16} className="text-brand-400" />
                    Orders
                  </span>
                  <ChevronDown
                    size={14}
                    className={`transition-transform duration-200 ${mobileOrdersOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {mobileOrdersOpen && (
                  <div className="pl-8 space-y-0.5">
                    {ordersLinks.map((link) => (
                      <Link
                        key={`mobile-orders-${link.label}`}
                        href={link.href}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <link.icon size={15} className="text-brand-400" />
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Authenticated: Account accordion */}
            {isAuthenticated && (
              <div>
                <button
                  onClick={() => setMobileAccountOpen((v) => !v)}
                  className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                >
                  <span className="flex items-center gap-3">
                    <UserCircle size={16} className="text-brand-400" />
                    Account
                  </span>
                  <ChevronDown
                    size={14}
                    className={`transition-transform duration-200 ${mobileAccountOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {mobileAccountOpen && (
                  <div className="pl-8 space-y-0.5">
                    {accountLinks.map((link) => (
                      <Link
                        key={`mobile-account-${link.label}`}
                        href={link.href}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <link.icon size={15} className="text-brand-400" />
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Bottom actions */}
            <div className="pt-3 border-t border-white/10 space-y-2">
              {!loading && (
                isAuthenticated ? (
                  <button
                    onClick={() => { setMobileOpen(false); handleSignOut(); }}
                    className="flex items-center justify-center gap-2 w-full text-center px-4 py-2.5 text-sm font-medium btn-outline"
                  >
                    <LogOut size={14} />
                    Sign Out
                  </button>
                ) : (
                  <Link
                    href="/sign-up-login"
                    onClick={() => setMobileOpen(false)}
                    className="block w-full text-center px-4 py-2.5 text-sm font-medium btn-outline"
                  >
                    Sign In
                  </Link>
                )
              )}
              <Link
                href="/order-wizard"
                onClick={() => setMobileOpen(false)}
                className="block w-full text-center px-4 py-2.5 text-sm font-semibold btn-primary"
              >
                Book an Ad
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}